import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { IconHeart, IconFolder, IconCheck } from './components/ui/Icons'

const API = import.meta.env.VITE_API_URL || '/api'

const wrap = {
  minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center',
  justifyContent: 'center', padding: '24px 16px',
  fontFamily: '-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif',
}
const inputStyle = {
  width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#fff',
  outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
const btn = (disabled) => ({
  background: '#ff3b30', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700,
  borderRadius: 12, padding: '14px', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.7 : 1, fontFamily: 'inherit', width: '100%',
})
const Logo = () => (
  <div style={{ textAlign: 'center', marginBottom: 32 }}>
    <Link to="/" style={{ textDecoration: 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <IconHeart size={36} color="#ef4444" fill="#ef4444" />
      </div>
      <p style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginTop: 8, letterSpacing: -0.5 }}>Manabi</p>
    </Link>
  </div>
)
const ErrBox = ({ msg }) => (
  <div style={{ background: 'rgba(255,59,48,0.12)', border: '1px solid rgba(255,59,48,0.3)', borderRadius: 10, padding: '10px 14px' }}>
    <p style={{ fontSize: 13, color: '#ff3b30' }}>{msg}</p>
  </div>
)

// ── Step 1: request a reset link ──────────────────────────────────────────────
export function ForgotPassword() {
  const [email, setEmail]     = useState('')
  const [sent, setSent]       = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)

  const submit = async e => {
    e.preventDefault()
    setLoading(true); setError(null)
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setSent(true)
    } catch (err) { setError(err.message) }
    setLoading(false)
  }

  return (
    <div style={wrap}><div style={{ width: '100%', maxWidth: 420 }}>
      <Logo />
      {sent ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <IconFolder size={36} color="#007aff" />
          </div>
          <p style={{ fontSize: 16, color: '#fff', fontWeight: 700, marginBottom: 8 }}>Revisá tu correo</p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
            Si existe una cuenta con ese email, te enviamos un link para restablecer la contraseña. Vence en 1 hora.
          </p>
          <p style={{ marginTop: 24 }}><Link to="/login" style={{ color: '#ff3b30', textDecoration: 'none', fontWeight: 600 }}>Volver a ingresar</Link></p>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: 4 }}>
            Ingresá tu email y te mandamos un link para crear una contraseña nueva.
          </p>
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
          {error && <ErrBox msg={error} />}
          <button type="submit" disabled={loading} style={btn(loading)}>{loading ? 'Enviando…' : 'Enviar link'}</button>
          <p style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>
            <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>← Volver</Link>
          </p>
        </form>
      )}
    </div></div>
  )
}

// ── Step 2: set a new password using the emailed token ────────────────────────
export function ResetPassword() {
  const [params]  = useSearchParams()
  const token     = params.get('token')
  const navigate  = useNavigate()
  const [pw, setPw]       = useState('')
  const [pw2, setPw2]     = useState('')
  const [done, setDone]   = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async e => {
    e.preventDefault()
    if (pw.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return }
    if (pw !== pw2)    { setError('Las contraseñas no coinciden.'); return }
    setLoading(true); setError(null)
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: pw }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setDone(true)
      setTimeout(() => navigate('/login'), 2500)
    } catch (err) { setError(err.message) }
    setLoading(false)
  }

  return (
    <div style={wrap}><div style={{ width: '100%', maxWidth: 420 }}>
      <Logo />
      {!token ? (
        <div style={{ textAlign: 'center' }}>
          <ErrBox msg='Link inválido. Pedí uno nuevo desde "¿Olvidaste tu contraseña?".' />
          <p style={{ marginTop: 20 }}><Link to="/recuperar" style={{ color: '#ff3b30', textDecoration: 'none', fontWeight: 600 }}>Pedir un link nuevo</Link></p>
        </div>
      ) : done ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <IconCheck size={36} color="#34c759" />
          </div>
          <p style={{ fontSize: 16, color: '#fff', fontWeight: 700, marginBottom: 8 }}>Contraseña actualizada</p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>Te llevamos al login…</p>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: 4 }}>Elegí tu contraseña nueva.</p>
          <input type="password" placeholder="Nueva contraseña (mín. 8)" value={pw}  onChange={e => setPw(e.target.value)}  required minLength={8} style={inputStyle} />
          <input type="password" placeholder="Repetir contraseña"        value={pw2} onChange={e => setPw2(e.target.value)} required minLength={8} style={inputStyle} />
          {error && <ErrBox msg={error} />}
          <button type="submit" disabled={loading} style={btn(loading)}>{loading ? 'Guardando…' : 'Guardar contraseña'}</button>
        </form>
      )}
    </div></div>
  )
}
